import { Platform, WEB_SEARCH_QUERY } from '@/env'
import { useSessionStore, setSessionState, SessionState } from '@/website/useSessionStore'
import { setStorageState } from '@/website/useStorageStore'
import { useWebsiteStore, setWebsiteState } from '@/website/useWebsiteStore'
import { SubscribeNiceModal } from '@/ui/useModal'
import React, { memo, useEffect, useMemo } from 'react'
import { throttle } from 'lodash-es'
import { createUrl } from '@/utils'
import { computedFontSize, createScreenRateMode } from '@/utils/rwd'
import { setLocale } from '@/i18n'

/** 視窗比例對照表 */
const screenRateMode = createScreenRateMode(String(Platform.VIEW_MODE), window.__screens)
const [fontSize] = computedFontSize(screenRateMode, window.innerWidth, window.__screens)
document.documentElement.style.fontSize = `${fontSize}px`

/** Runtime 初始化 */
const initWebSite = () => {
  const { screens, isMobile } = useWebsiteStore.getState()

  // url query
  const url = createUrl(document.URL)
  const query = useSessionStore.getState()?.params || {}
  Object.assign(query, (Object.fromEntries(url?.searchParams?.entries() || []) || {}) as SessionState['params']) // 合併 session cache

  /** 離開時間過久判斷 */
  {
    let leaveTime = 0
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        leaveTime = Date.now()
      } else if (leaveTime !== 0) {
        const duration = Date.now() - leaveTime
        // 離開超過兩小重整
        if (duration > 2 * 60 * 60 * 1000) window.location.reload()
      }
    })
  }

  /** 視窗更新 */
  {
    const computedStyle = getComputedStyle(document.body)
    const style = document?.documentElement?.style || document.body?.style

    /** 視窗大小 */
    let prevIsMobileScreen = window.innerWidth < screens.sm
    let lockResize = 0
    // 延遲更新
    const updateSize = throttle(
      (width, height) => {
        setWebsiteState((state) => {
          if (state.skipReFontSize) return { size: { width, height } }
          const [fontSize] = computedFontSize(screenRateMode, width, screens)
          return { size: { width, height }, fontSize }
        })
        style.setProperty('--svw', `${width}px`)
        style.setProperty('--svh', `${height}px`)
      },
      250,
      { leading: true, trailing: true },
    )
    // Window Resize
    let prevMode = ''
    const onResize = () => {
      // 鎖定中，不更新
      if (lockResize > Date.now()) return
      // 有鎖定輸入欄位，不更新
      if (isMobile && /input|textarea/i.test(document?.activeElement?.tagName || '')) {
        lockResize = Date.now() + 2000
        return
      }
      const width = window.innerWidth
      const height = window.innerHeight
      // 延遲更新 size
      updateSize(width, height)
      // 即時更新 isMobileScreen
      const isMobileScreen = width < screens.sm
      if (prevIsMobileScreen !== isMobileScreen) {
        prevIsMobileScreen = isMobileScreen
        setWebsiteState({ isMobileScreen })
      }
      // 即時更新 fontSize，需要依照版型自定邏輯
      const skipReFontSize = useWebsiteStore.getState().skipReFontSize
      if (skipReFontSize) return
      const [fontSize, mode] = computedFontSize(screenRateMode, width, screens)
      if (prevMode !== mode) {
        prevMode = mode
        setWebsiteState({ size: { width, height }, fontSize })
      }
      document.documentElement.style.fontSize = `${fontSize}px`
    }
    setWebsiteState({ isMobileScreen: prevIsMobileScreen })
    onResize()
    window.addEventListener('resize', onResize)

    /** 安全範圍 */
    const updateSafeArea = () => {
      const safeArea = {
        top: parseInt(String(computedStyle.getPropertyValue('--area-top')).replace(/[^0-9]/g, '')) || 0,
        right: parseInt(String(computedStyle.getPropertyValue('--area-right')).replace(/[^0-9]/g, '')) || 0,
        bottom: parseInt(String(computedStyle.getPropertyValue('--area-bottom')).replace(/[^0-9]/g, '')) || 0,
        left: parseInt(String(computedStyle.getPropertyValue('--area-left')).replace(/[^0-9]/g, '')) || 0,
      }
      setWebsiteState({ safeArea })
    }
    updateSafeArea()
    window.addEventListener('orientationchange', updateSafeArea)
  }

  /** 網址參數處理 */
  {
    setWebsiteState((state) => {
      // 安全範圍
      if (url?.searchParams?.get(WEB_SEARCH_QUERY.SAFE_AREA)) {
        const safeArea = url?.searchParams?.get(WEB_SEARCH_QUERY.SAFE_AREA)!.split(',').map(Number)
        state.safeArea = { top: safeArea[0], right: safeArea[1], bottom: safeArea[2], left: safeArea[3] }
        const style = document?.documentElement?.style || document.body?.style
        style?.setProperty('--safe-top', `${safeArea[0]}px`)
        style?.setProperty('--safe-right', `${safeArea[1]}px`)
        style?.setProperty('--safe-bottom', `${safeArea[2]}px`)
        style?.setProperty('--safe-left', `${safeArea[3]}px`)
      }

      // 簡易版型
      if (WEB_SEARCH_QUERY.SIMPLE_PAGE in query) {
        state.isSimplePage = true
      }
    })

    // 需要存進 session 的參數
    setSessionState((state) => {
      // 避免三方狀態因為跳轉消失
      const keys = Object.values(WEB_SEARCH_QUERY)
      Object.keys(query).forEach((key) => {
        if (keys.includes(key as WEB_SEARCH_QUERY)) state.params[key as WEB_SEARCH_QUERY] = query[key as keyof typeof query]
      })
    })

    // 語系
    if (WEB_SEARCH_QUERY.LANG in query && Platform.LOCALES.includes(query[WEB_SEARCH_QUERY.LANG]!)) {
      setLocale(query[WEB_SEARCH_QUERY.LANG]!).catch(console.warn)
    }
  }

  /** 老客戶 */
  {
    if (document.cookie.includes('oldUser')) {
      setWebsiteState({ isOldUser: true })
    }
  }

  /** Debug Log */
  {
    console.log('%c網頁初始數據', 'background:red;color:white', useWebsiteStore.getState(), screenRateMode)
  }

  /**
   * 還有其他初始化，因為 service 而無法放在這裡的，通常都在 common/model/{service}/core 中
   */
}
initWebSite()

/**
 * 統一的網站初始化行為，不區分 onbet | okbet
 */
export const WebInitialized = memo(() => {
  /** 水合階段結束 */
  useEffect(() => void setWebsiteState((state) => ({ init: true, visitCount: state.visitCount + 1 })), [])

  return (
    <>
      {/*監聽彈窗狀態*/}
      <SubscribeNiceModal />
    </>
  )
})

WebInitialized.displayName = 'WebInitialized'
